import {type ComponentType, lazy, Suspense, useContext, useState} from "react";
import {UsedBlocksContext} from "@/context/onDemandBlocks";
import {loadAllBlocks, registerBlock} from "@/utils/onDemandBlocks";
import B2TexteWYSIWYG from "./B2TexteWYSIWYG";
import B2TexteWYSIWYGNew from "./B2TexteWYSIWYGNew";
import B3Citation from "./B3Citation";
import B4Images from "./B4Images";
import B5SliderImages from "./B5SliderImages";
import B7Accordion from "./B7Accordion";
import B8Inserts from "./B8Inserts";
import B9ImageTextEncarts from "./B9ImageTextEncarts";
import B10PushMail from "./B10PushMail";
import B10PushMailVM from "./B10PushMailVM";
import B12CrossContent from "./B12CrossContent";
import B14Rubriques from "./B14Rubriques";
import B16Articles from "./B16Articles";
import B17PushEntries from "./B17PushEntries";
import B18Questions from "./B18Questions";

// The map (maplibre, ~1.2 MB) and the advanced search are used by no content in production (0 of 1,355) yet were
// bundled with every page: they load only when a content has the block. The server renders such a block on the
// client (renderToString does not wait for a lazy module): neither carries text to index.
// biome-ignore lint/suspicious/noExplicitAny: registre hétérogène, chaque composant de section a des props différentes et incompatibles entre elles (même choix que B15Promote/pushes/index.ts)
const lazyBlock = (load: () => Promise<{default: ComponentType<any>}>) => {
  const Block = lazy(load);
  // biome-ignore lint/suspicious/noExplicitAny: props transmises telles quelles au composant lazy, dont le type varie selon l'appelant
  return (props: any) => (
    <Suspense fallback={null}>
      <Block {...props} />
    </Suspense>
  );
};
const B19Maplibre = lazyBlock(() => import("./B19Maplibre"));
const SectionPlanTrip = lazyBlock(() => import("./SectionPlanTrip"));

// Blocks that almost no content uses (published contents of fr, en, es and it, October 2026: 0 of 1,332 for the promote block, the video and the sponsored
// article; 1 each for the text and image and the FAQ): their code is loaded when a page has the block, not with every page. Unlike the map, they carry TEXT that
// must be in the HTML the server sends, and renderToString does not wait for a lazy module. So:
// - on the server the modules are loaded once, before the first request (the `await` at the end of this file), and a block is then drawn as a plain
//   component, in the same Suspense boundary as in the browser so that the markup is the same. The server notes which blocks the page draws;
// - the browser loads those modules BEFORE it hydrates (entry.client.tsx, utils/onDemandBlocks.ts): hydrating while a module is on its way throws the
//   server HTML of the block away as soon as a component above it re-renders;
// - a page reached by a click has no server HTML: the block is drawn once its module is there (at once if it already came). The component used by a
//   mounted block does not change afterwards (it would be remounted).
// No static import on purpose: a bundler keeps a module that is imported statically, and its `import()` then lands in the same chunk.
// The other rare blocks (the questions, on the home page of fr; the push entries, on the home pages and the categories) stay static: pages that people open.
// biome-ignore lint/suspicious/noExplicitAny: registre hétérogène, chaque composant de section a des props différentes et incompatibles entre elles
type Block = ComponentType<any>;
const onDemand = (name: string, load: () => Promise<{default: Block}>) => {
  let ready: Block | undefined;
  const loadOnce = () =>
    load().then((module) => {
      ready = module.default;
      return module;
    });
  registerBlock(name, loadOnce);
  const Lazy = lazy(loadOnce);
  // biome-ignore lint/suspicious/noExplicitAny: props transmises telles quelles, dont le type varie selon l'appelant
  return (props: any) => {
    useContext(UsedBlocksContext)?.add(name);
    const [Inner] = useState<Block>(() => ready ?? Lazy);
    return (
      <Suspense fallback={null}>
        <Inner {...props} />
      </Suspense>
    );
  };
};
const B6TextAndImage = onDemand("B6TextAndImage", () => import("./B6TextAndImage"));
const B11SponsoredArticle = onDemand(
  "B11SponsoredArticle",
  () => import("./B11SponsoredArticle"),
);
const B13Video = onDemand("B13Video", () => import("./B13Video"));
const B15Promote = onDemand("B15Promote", () => import("./B15Promote"));
const B21FAQ = onDemand("B21FAQ", () =>
  import("./B21Faq").then((module) => ({default: module.B21FAQ})),
);

// biome-ignore lint/suspicious/noExplicitAny: registre hétérogène, chaque composant de section a des props différentes et incompatibles entre elles (même choix que B15Promote/pushes/index.ts)
export const sectionsComponents: Record<string, ComponentType<any>> = {
  // "section--herobanner": B1Herobanner,
  "section--wysiwyg": B2TexteWYSIWYG,
  "section--wysiwyg-new": B2TexteWYSIWYGNew,
  "section--quote": B3Citation,
  "section--images": B4Images,
  "section--slider-images": B5SliderImages,
  "section--text-image": B6TextAndImage,
  "section--accordions": B7Accordion,
  "section--inserts": B8Inserts,
  "section--image-text-inserts": B9ImageTextEncarts,
  "section--push-vm": B10PushMailVM,
  "section--push-maillage": B10PushMail,
  "section--sponsor-article": B11SponsoredArticle,
  "section--cross-contenu": B12CrossContent,
  "section--video": B13Video,
  "section--sections": B14Rubriques,
  "section--highlight": B15Promote,
  "section--push-content": B16Articles,
  "section--push-entries": B17PushEntries,
  "section--question-answer": B18Questions,
  "section--maplibre": B19Maplibre,
  "section--advanced-search": SectionPlanTrip,
  "section--faq": B21FAQ,
};

// The server starts answering once these are loaded (the server build is an ES module: top-level await)
if (import.meta.env.SSR) {
  await loadAllBlocks();
}
